import { useEffect, useState } from 'react';
import { useSettings } from '@/contexts/SettingsContext';
import { translations, type Language } from '@/lib/translations';

// Key used to persist guest (unauthenticated) language choice
const GUEST_LANG_KEY = 'guestLanguage';

/**
 * Dispatch this event anywhere to switch the guest language without a page reload.
 * e.g. window.dispatchEvent(new Event('guestLanguageChange'))
 */
export const GUEST_LANG_EVENT = 'guestLanguageChange';

/**
 * Read the guest language from localStorage (safe: returns null on SSR or error)
 */
function getStoredGuestLanguage(): Language | null {
  try {
    const stored = localStorage.getItem(GUEST_LANG_KEY);
    if (stored === 'en' || stored === 'fr') return stored;
  } catch {
    // localStorage unavailable (private mode, etc.)
  }
  return null;
}

/**
 * Persist guest language and notify all useTranslation subscribers
 */
export function setGuestLanguage(lang: Language): void {
  try {
    localStorage.setItem(GUEST_LANG_KEY, lang);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event(GUEST_LANG_EVENT));
}

/**
 * Hook to access translations based on user's language setting.
 *
 * Priority order:
 *  1. Authenticated user's saved language (from SettingsContext)
 *  2. Guest language stored in localStorage (set via setGuestLanguage)
 *  3. Default: 'en'
 *
 * Usage: const { t, lang } = useTranslation();
 * Then: t('common.save') or t('dashboard.title')
 */
export function useTranslation() {
  const { settings } = useSettings();

  // Track guest language so the hook re-renders when it changes
  const [guestLang, setGuestLang] = useState<Language | null>(getStoredGuestLanguage);

  useEffect(() => {
    const handler = () => setGuestLang(getStoredGuestLanguage());
    window.addEventListener(GUEST_LANG_EVENT, handler);
    return () => window.removeEventListener(GUEST_LANG_EVENT, handler);
  }, []);

  // Authenticated user setting wins; otherwise fall back to guest preference
  const lang: Language = settings?.language || guestLang || 'en';

  /**
   * Get translation for a given key
   * @param key - Dot-notation key (e.g., 'common.save', 'dashboard.title')
   * @returns Translated string
   */
  const t = (key: string): string => {
    const keys = key.split('.');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = translations[lang];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key} for language: ${lang}`);
        return key;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  /**
   * Get category label translation
   */
  const tc = (categoryId: string): string => t(`categories.${categoryId}`);

  /**
   * Get day name translation
   */
  const td = (dayIndex: number): string => {
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(`days.${dayKeys[dayIndex]}`);
  };

  /**
   * Get month name translation
   */
  const tm = (monthIndex: number): string => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december',
    ];
    return t(`months.${monthKeys[monthIndex]}`);
  };

  return {
    t,      // General translation
    tc,     // Category translation
    td,     // Day translation
    tm,     // Month translation
    lang,   // Current language
  };
}